import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { RefreshCw, Download, CheckCircle2 } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { fmtEur } from "@/lib/lnwHours";

type Customer = Tables<"customers">;
type Contact = Tables<"customer_contacts">;
type Project = Tables<"projects">;

type Voucher = {
  id: string;
  voucherNumber: string | null;
  voucherDate: string | null;
  voucherStatus: string | null;
  contactId: string | null;
  contactName: string | null;
  totalAmount: number | null;
  currency: string;
  imported: { id: string; name: string } | null;
};

type Detail = {
  id: string;
  voucherNumber: string | null;
  customerId: string | null;
  customerName: string | null;
  contactPerson: string | null;
  suggestedName: string;
  projectHint: string | null;
  netAmount: number | null;
  hourlyRate: number | null;
  hours: number | null;
};

const NEW_PROJECT = "__new__";

const readError = async (error: unknown) => {
  if (error instanceof FunctionsHttpError) {
    try {
      const body = await error.context.json();
      return typeof body?.error === "string" ? body.error : JSON.stringify(body);
    } catch {
      return error.message;
    }
  }
  return (error as Error)?.message ?? "Unbekannter Fehler";
};

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "–");

const AdminLexware = () => {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);

  const [detail, setDetail] = useState<Detail | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState({
    customerId: "",
    projectId: "",
    newProjectName: "",
    contactId: "none",
    name: "",
    orderNumber: "",
    hourlyRate: "",
    budgetAmount: "",
    budgetHours: "",
  });

  const loadLocal = async () => {
    const [{ data: c }, { data: p }, { data: k }] = await Promise.all([
      supabase.from("customers").select("*").order("company"),
      supabase.from("projects").select("*").order("name"),
      supabase.from("customer_contacts").select("*").order("name"),
    ]);
    setCustomers(c ?? []);
    setProjects(p ?? []);
    setContacts(k ?? []);
  };

  const loadVouchers = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("lexware-order-confirmations", { body: { action: "list" } });
    setLoading(false);
    if (error) {
      toast.error(`Auftragsbestätigungen konnten nicht geladen werden: ${await readError(error)}`);
      return;
    }
    setVouchers((data?.items ?? []) as Voucher[]);
  };

  useEffect(() => {
    void loadLocal();
    void loadVouchers();
  }, []);

  const syncContacts = async () => {
    setSyncing(true);
    const { data, error } = await supabase.functions.invoke("lexware-sync-contacts", { body: {} });
    setSyncing(false);
    if (error) {
      toast.error(`Abgleich fehlgeschlagen: ${await readError(error)}`);
      return;
    }
    toast.success(
      `${data.created} Kunden neu, ${data.updated} aktualisiert, ${data.skipped} übersprungen · Ansprechpartner: ${data.contactsCreated} neu, ${data.contactsUpdated} aktualisiert`,
    );
    void loadLocal();
    void loadVouchers();
  };

  const openImport = async (v: Voucher) => {
    setDialogOpen(true);
    setDetail(null);
    const { data, error } = await supabase.functions.invoke("lexware-order-confirmations", {
      body: { action: "detail", id: v.id },
    });
    if (error) {
      setDialogOpen(false);
      toast.error(`Details konnten nicht geladen werden: ${await readError(error)}`);
      return;
    }
    const d = data as Detail;
    setDetail(d);
    const customerId = d.customerId ?? "";
    const customerProjects = projects.filter((p) => p.customer_id === customerId);
    setForm({
      customerId,
      projectId: customerProjects.length === 1 ? customerProjects[0].id : "",
      newProjectName: d.projectHint ?? "",
      contactId: "none",
      name: d.suggestedName || v.voucherNumber || "Auftrag",
      orderNumber: d.voucherNumber ?? v.voucherNumber ?? "",
      hourlyRate: d.hourlyRate != null ? String(d.hourlyRate) : "",
      budgetAmount: d.netAmount != null ? String(d.netAmount) : "",
      budgetHours: d.hours != null ? String(d.hours) : "",
    });
  };

  const contactsForCustomer = useMemo(
    () => contacts.filter((c) => c.customer_id === form.customerId),
    [contacts, form.customerId],
  );
  const projectsForCustomer = useMemo(
    () => projects.filter((p) => p.customer_id === form.customerId),
    [projects, form.customerId],
  );

  const runImport = async (createTimesheet = false) => {
    if (!detail) return;
    if (!form.customerId) {
      toast.error("Bitte einen Kunden zuordnen.");
      return;
    }
    if (!form.projectId || (form.projectId === NEW_PROJECT && !form.newProjectName.trim())) {
      toast.error("Bitte ein Projekt wählen oder einen Namen für das neue Projekt angeben.");
      return;
    }
    setImporting(true);
    const { data, error } = await supabase.functions.invoke("lexware-import-order", {
      body: {
        lexwareId: detail.id,
        customerId: form.customerId,
        projectId: form.projectId === NEW_PROJECT ? "" : form.projectId,
        newProjectName: form.projectId === NEW_PROJECT ? form.newProjectName.trim() : "",
        contactId: form.contactId === "none" ? null : form.contactId,
        name: form.name.trim(),
        orderNumber: form.orderNumber.trim(),
        hourlyRate: form.hourlyRate,
        budgetAmount: form.budgetAmount,
        budgetHours: form.budgetHours,
      },
    });
    setImporting(false);
    if (error) {
      toast.error(`Übernahme fehlgeschlagen: ${await readError(error)}`);
      return;
    }
    toast.success("Auftrag angelegt");
    setDialogOpen(false);
    void loadLocal();
    void loadVouchers();
    if (createTimesheet && data?.orderId) {
      navigate(`/admin/lnw/neu?order=${data.orderId}`);
    }
  };

  return (
    <>
      <Helmet>
        <title>Lexware | Leistungsnachweise</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Lexware</h1>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={loadVouchers} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
          </Button>
          <Button className="gap-2" onClick={syncContacts} disabled={syncing}>
            <Download className="h-4 w-4" /> {syncing ? "Abgleich läuft…" : "Kunden abgleichen"}
          </Button>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Kunden und Ansprechpartner</CardTitle>
          <CardDescription>
            Der Abgleich holt Kunden und deren Ansprechpartner aus Lexware. Lexware bleibt die führende Quelle, es wird nichts
            zurückgeschrieben. Aktuell {customers.length} Kunden und {contacts.length} Ansprechpartner im Adminbereich.
          </CardDescription>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Auftragsbestätigungen</CardTitle>
          <CardDescription>Wählen Sie aus, welche Bestätigung zu einem Auftrag werden soll.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nummer</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead>Kunde</TableHead>
                <TableHead className="text-right">Summe netto</TableHead>
                <TableHead className="w-48 text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">Laden…</TableCell></TableRow>
              ) : vouchers.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">Keine Auftragsbestätigungen gefunden.</TableCell></TableRow>
              ) : (
                vouchers.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell className="font-medium">{v.voucherNumber ?? "–"}</TableCell>
                    <TableCell>{fmtDate(v.voucherDate)}</TableCell>
                    <TableCell>{v.contactName ?? "–"}</TableCell>
                    <TableCell className="text-right">{v.totalAmount != null ? fmtEur(v.totalAmount) : "–"}</TableCell>
                    <TableCell className="text-right">
                      {v.imported ? (
                        <Badge variant="secondary" className="gap-1">
                          <CheckCircle2 className="h-3.5 w-3.5" /> übernommen
                        </Badge>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => openImport(v)}>
                          Als Auftrag übernehmen
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Auftrag aus Auftragsbestätigung</DialogTitle>
          </DialogHeader>
          {!detail ? (
            <p className="py-8 text-center text-muted-foreground">Daten aus Lexware werden geladen…</p>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {detail.voucherNumber} · {detail.customerName ?? "Kunde unbekannt"}
                {detail.contactPerson ? ` · ${detail.contactPerson}` : ""}
              </p>

              <div className="space-y-2">
                <Label>Kunde *</Label>
                <Select
                  value={form.customerId}
                  onValueChange={(v) => setForm((p) => ({ ...p, customerId: v, projectId: "", contactId: "none" }))}
                >
                  <SelectTrigger><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!detail.customerId && (
                  <p className="text-xs text-muted-foreground">
                    Kein passender Kunde gefunden. Führen Sie zuerst den Kundenabgleich aus oder wählen Sie manuell.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Projekt *</Label>
                <Select value={form.projectId} onValueChange={(v) => setForm((p) => ({ ...p, projectId: v }))} disabled={!form.customerId}>
                  <SelectTrigger><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
                  <SelectContent>
                    {projectsForCustomer.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                    <SelectItem value={NEW_PROJECT}>+ Neues Projekt anlegen</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.projectId === NEW_PROJECT && (
                <div className="space-y-2">
                  <Label htmlFor="lx-project">Name des neuen Projekts *</Label>
                  <Input id="lx-project" value={form.newProjectName} onChange={(e) => setForm({ ...form, newProjectName: e.target.value })} />
                </div>
              )}

              <div className="space-y-2">
                <Label>Ansprechpartner</Label>
                <Select value={form.contactId} onValueChange={(v) => setForm({ ...form, contactId: v })} disabled={!form.customerId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kein Ansprechpartner</SelectItem>
                    {contactsForCustomer.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lx-name">Auftragsbezeichnung *</Label>
                <Input id="lx-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lx-number">Auftragskennung</Label>
                <Input id="lx-number" value={form.orderNumber} onChange={(e) => setForm({ ...form, orderNumber: e.target.value })} />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="lx-rate">Stundensatz (€ netto)</Label>
                  <Input id="lx-rate" inputMode="decimal" value={form.hourlyRate} onChange={(e) => setForm({ ...form, hourlyRate: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lx-amount">Auftragssumme (€ netto)</Label>
                  <Input id="lx-amount" inputMode="decimal" value={form.budgetAmount} onChange={(e) => setForm({ ...form, budgetAmount: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lx-hours">Stundenkontingent</Label>
                  <Input id="lx-hours" inputMode="decimal" value={form.budgetHours} onChange={(e) => setForm({ ...form, budgetHours: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button variant="secondary" onClick={() => runImport(false)} disabled={importing || !detail}>
              {importing ? "Übernehmen…" : "Nur Auftrag anlegen"}
            </Button>
            <Button onClick={() => runImport(true)} disabled={importing || !detail}>
              {importing ? "Übernehmen…" : "Auftrag anlegen + Leistungsnachweis"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminLexware;
